import React, { useState, useEffect, useRef } from 'react';
import {
  NavigationScreen,
  RecordingSession,
  AppSettings,
  CaptureSource,
  QualityConfig,
  ProcessingStep
} from './types';
import { StorageService, SAMPLE_RECORDINGS } from './services/storageService';
import { recordingEngine, RecordingState } from './services/recordingEngine';
import { tileDetector } from './services/tileDetector';
import { generateId, formatDuration, generateThumbnailFromBlob } from './utils/helpers';
import { Square } from 'lucide-react';

// Components & Screens
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { RecordingStudioScreen } from './screens/RecordingStudioScreen';
import { PodcastStudioScreen } from './screens/PodcastStudioScreen';
import { ActiveRecordingScreen } from './screens/ActiveRecordingScreen';
import { ProcessingStudioScreen } from './screens/ProcessingStudioScreen';
import { SeparateVideosScreen } from './screens/SeparateVideosScreen';
import { ParticipantReviewScreen } from './screens/ParticipantReviewScreen';
import { RecordingDetailsScreen } from './screens/RecordingDetailsScreen';
import { ExportCenterScreen } from './screens/ExportCenterScreen';
import { RecordingLibraryScreen } from './screens/RecordingLibraryScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { MobileGuestMeetingScreen } from './screens/MobileGuestMeetingScreen';
import { GroupMeetingScreen } from './screens/GroupMeetingScreen';
import { MobileGuestHubScreen } from './screens/MobileGuestHubScreen';
import { SchedulerScreen } from './screens/SchedulerScreen';
import { LandingScreen } from './screens/LandingScreen';
import { Capacitor } from '@capacitor/core';
import { webrtcGroupMeetingService } from './services/webrtcGroupMeetingService';
import { webrtcMeetingService } from './services/webrtcMeetingService';
import { audioService } from './services/audioService';

const INITIAL_PROCESSING_STEPS: ProcessingStep[] = [
  { id: 'save', title: 'Finalize Original Recording', description: 'Muxing media chunks and committing local video container', status: 'pending', progress: 0 },
  { id: 'probe', title: 'Probe Video & Audio Dimensions', description: 'Inspecting resolution, framerate, and audio channels', status: 'pending', progress: 0 },
  { id: 'detect', title: 'Detect Participant Video Tiles', description: 'Offline computer-vision grid clustering and boundary detection', status: 'pending', progress: 0 },
  { id: 'track', title: 'Track Candidate Positions & Layout', description: 'Correlating tile stability, active speaker frames and avatars', status: 'pending', progress: 0 },
  { id: 'thumbnails', title: 'Generate Participant Previews', description: 'Extracting candidate thumbnails for verification', status: 'pending', progress: 0 },
];

const HostStudioApp: React.FC<{
  initialScreen: NavigationScreen;
  initialRoomCode?: string;
}> = ({ initialScreen, initialRoomCode }) => {
  const [settings, setSettings] = useState<AppSettings>(() => StorageService.getSettings());
  const [recordings, setRecordings] = useState<RecordingSession[]>(() => StorageService.getRecordings());
  const [currentScreen, setCurrentScreen] = useState<NavigationScreen>(initialScreen);
  const [joinMeetingCode, setJoinMeetingCode] = useState<string | undefined>(initialRoomCode);
  const [selectedSession, setSelectedSession] = useState<RecordingSession | null>(
    recordings.length > 0 ? recordings[0] : null
  );

  // Active Capture State
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const [activeSourceType, setActiveSourceType] = useState<'screen' | 'window' | 'camera' | 'screen-camera'>('screen');
  const [recordingAspectRatio, setRecordingAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const [studioInitialTab, setStudioInitialTab] = useState<'screen' | 'window' | 'camera'>('screen');
  const [podcastInitialFormat, setPodcastInitialFormat] = useState<'vodcast-single' | 'vodcast-duo' | 'vodcast-online' | 'audio-only'>('vodcast-single');
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    durationSec: 0,
    recordedBytes: 0,
    fps: 60,
    health: 'optimal',
    sourceName: 'Desktop Screen'
  });

  // Processing Studio State
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>(INITIAL_PROCESSING_STEPS);
  const [processingIndex, setProcessingIndex] = useState(0);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('Initializing local processing...');
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [currentProcessingTitle, setCurrentProcessingTitle] = useState('Active Meeting');

  // Diagnostics & Disk space
  const [availableDiskSpaceGb, setAvailableDiskSpaceGb] = useState(145);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize theme and disk space
  useEffect(() => {
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }

    if (window.electronAPI && typeof window.electronAPI.getFreeDiskSpace === 'function') {
      window.electronAPI.getFreeDiskSpace(settings.storagePath).then(res => {
        setAvailableDiskSpaceGb(res.freeGb);
      });
    }
  }, [settings.theme, settings.storagePath]);

  const handleUpdateSettings = (newSettings: Partial<AppSettings>) => {
    const updated = StorageService.saveSettings(newSettings);
    setSettings(updated);
  };

  const toggleTheme = () => {
    const nextTheme = settings.theme === 'dark' ? 'light' : 'dark';
    handleUpdateSettings({ theme: nextTheme });
  };

  // Global Hardware Watchdog: Ensure Camera & Mic are COMPLETELY turned off when navigating away from recording / meeting screens
  useEffect(() => {
    const isCaptureScreen =
      currentScreen === 'studio' ||
      currentScreen === 'active-recording' ||
      currentScreen === 'podcast-studio' ||
      currentScreen === 'group-meeting';

    if (!isCaptureScreen) {
      // 1. If not actively recording, stop and release activeStream tracks
      if (!isRecordingActive && activeStream) {
        activeStream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            console.warn('Error stopping active track:', e);
          }
        });
        setActiveStream(null);
      }

      // 2. Stop screen share if any
      if (!isRecordingActive && recordingEngine.isScreenSharing()) {
        try {
          recordingEngine.stopScreenShare();
        } catch {}
      }

      // 3. Guarantee all WebRTC meeting & group meeting connections and hardware tracks are disconnected
      try {
        webrtcGroupMeetingService.disconnect();
      } catch {}

      try {
        webrtcMeetingService.disconnect();
      } catch {}

      // 4. Stop any audio meters
      try {
        audioService.stopMeters();
      } catch {}
    }
  }, [currentScreen, isRecordingActive, activeStream]);

  // Start Recording
  const handleStartRecording = async (
    source: CaptureSource | null,
    quality: QualityConfig,
    cameraStream?: MediaStream | null
  ) => {
    try {
      setActiveSourceType(source?.type || 'screen');
      setRecordingAspectRatio(source?.aspectRatio || '16:9');

      const stream = await recordingEngine.startRecording(
        source,
        {
          micDeviceId: settings.defaultMicId,
          micLabel: 'Microphone',
          micEnabled: true,
          systemAudioEnabled: source?.type === 'camera' ? false : settings.enableSystemAudio,
          systemAudioStatus: 'active'
        },
        quality,
        (state) => {
          setRecordingState(state);
        },
        cameraStream
      );

      setActiveStream(stream);
      setIsRecordingActive(true);
      setCurrentScreen('active-recording');

      // Automatically enter Fullscreen as soon as recording starts
      if (window.electronAPI && typeof window.electronAPI.setFullScreen === 'function') {
        window.electronAPI.setFullScreen(true);
      } else if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } catch (err) {
      console.error('Failed to start recording session:', err);
      alert('Screen or webcam recording permission was not granted or capture source was cancelled.');
    }
  };

  const handlePauseRecording = () => recordingEngine.pause();
  const handleResumeRecording = () => recordingEngine.resume();
  const handleToggleScreenShare = async () => {
    if (recordingEngine.isScreenSharing()) {
      recordingEngine.stopScreenShare();
    } else {
      await recordingEngine.startScreenShare();
    }
  };

  // Stop Recording & Process
  const handleStopRecording = async () => {
    setIsRecordingActive(false);

    // Restore standard window mode when recording finishes
    if (window.electronAPI && typeof window.electronAPI.setFullScreen === 'function') {
      window.electronAPI.setFullScreen(false);
    } else if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }

    setCurrentScreen('processing');
    setIsProcessingComplete(false);
    setProcessingSteps(INITIAL_PROCESSING_STEPS);
    const isSolo = activeSourceType === 'camera';
    const isShorts = isSolo && recordingAspectRatio === '9:16';
    const titlePrefix = isShorts ? 'SoloShorts_9x16' : isSolo ? 'SoloRecording' : 'Meeting';
    setCurrentProcessingTitle(`${titlePrefix}_${new Date().toLocaleTimeString().replace(/:/g, '')}`);

    const result = await recordingEngine.stop();
    setActiveStream(null);

    // Run local pipeline
    await executeProcessingPipeline(
      result.blob,
      result.durationSec,
      result.width,
      result.height,
      result.fps,
      undefined,
      undefined,
      isSolo,
      recordingAspectRatio
    );
  };

  const executeProcessingPipeline = async (
    blob: Blob,
    durationSec: number,
    width: number,
    height: number,
    fps: number,
    customTitle?: string,
    existingFilePath?: string,
    isSoloCamera?: boolean,
    sessionAspectRatio: '16:9' | '9:16' | '1:1' = '16:9'
  ) => {
    const steps = [...INITIAL_PROCESSING_STEPS];
    const updateStep = (index: number, status: 'running' | 'completed' | 'error', percent: number, msg: string) => {
      steps[index].status = status;
      steps[index].progress = percent;
      setProcessingSteps([...steps]);
      setProcessingIndex(index);
      setOverallPercentage(Math.round(((index + (status === 'completed' ? 1 : 0.5)) / steps.length) * 100));
      setProcessingMessage(msg);
    };

    // Step 1: Save
    updateStep(0, 'running', 40, 'Finalizing local video path...');
    const defaultFilename = isSoloCamera
      ? (sessionAspectRatio === '9:16' ? `Solo_Shorts_${Date.now()}.mp4` : `Solo_${Date.now()}.mp4`)
      : `Meeting_${Date.now()}.mp4`;
    let filePath = existingFilePath || `${settings.storagePath}\\${defaultFilename}`;
    const blobUrl = URL.createObjectURL(blob);

    if (!existingFilePath && window.electronAPI && typeof window.electronAPI.saveRecordingBlob === 'function') {
      try {
        const buffer = await blob.arrayBuffer();
        const saved = await window.electronAPI.saveRecordingBlob(generateId(), defaultFilename, buffer);
        filePath = saved.filePath;
      } catch (e) {
        console.warn('Native save fallback:', e);
      }
    }
    updateStep(0, 'completed', 100, 'Original media successfully ready.');

    // Step 2: Probe
    updateStep(1, 'running', 60, `Probing video streams (${width}x${height} @ ${fps}fps)...`);
    await new Promise(r => setTimeout(r, 400));
    updateStep(1, 'completed', 100, `Video stream validated (${durationSec}s duration).`);

    // Step 3: Detect Tiles / Setup Speaker Tracks
    updateStep(2, 'running', 50, isSoloCamera
      ? (sessionAspectRatio === '9:16' ? 'Configuring 9:16 vertical Shorts track...' : 'Configuring solo speaker track...')
      : 'Analyzing participant tiles and speaker layout...');
    const analysis = await tileDetector.analyzeVideo(blobUrl, durationSec, (msg, pct) => {
      setProcessingMessage(msg);
    });

    let participants = analysis.participants;
    let layout = analysis.detectedLayout;

    if (isSoloCamera) {
      layout = 'single_speaker';
      const isShorts = sessionAspectRatio === '9:16';
      participants = [
        {
          id: generateId('solo'),
          name: isShorts ? 'You (9:16 Shorts/Reel)' : 'You (Solo Speaker)',
          role: 'host',
          crop: (isShorts && width > height)
            ? { x: 0.3417, y: 0, width: 0.3166, height: 1.0 }
            : { x: 0, y: 0, width: 1, height: 1 },
          confidence: 100,
          isIncluded: true,
          activeSpeakerRatio: 100,
          visibilitySegments: [{ startSec: 0, endSec: durationSec }],
          thumbnailUrl: analysis.sampleFrameUrl
        }
      ];
    }
    updateStep(2, 'completed', 100, `Configured ${participants.length} speaker track.`);

    // Step 4: Track
    updateStep(3, 'running', 70, 'Tracking bounding boxes across duration...');
    await new Promise(r => setTimeout(r, 500));
    updateStep(3, 'completed', 100, 'Bounding box stability verified.');

    // Step 5: Previews
    updateStep(4, 'running', 80, 'Preparing participant preview clips...');
    await new Promise(r => setTimeout(r, 400));
    updateStep(4, 'completed', 100, 'Participant preview clips ready.');

    // Create session record
    const dateStr = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const sessionTitle = customTitle || (
      isSoloCamera
        ? (sessionAspectRatio === '9:16' ? `Solo Shorts (9:16) — ${dateStr}` : `Solo Recording — ${dateStr}`)
        : `Session — ${dateStr}`
    );
    const newSession: RecordingSession = {
      id: generateId('rec'),
      title: sessionTitle,
      createdAt: new Date().toISOString(),
      durationSec,
      fileSize: blob.size,
      filePath,
      blobUrl,
      resolution: { width, height },
      fps,
      audioChannels: 2,
      status: 'ready',
      aspectRatio: sessionAspectRatio,
      detectedLayout: layout,
      thumbnailUrl: analysis.sampleFrameUrl,
      participants,
      exportedClips: []
    };

    StorageService.addRecording(newSession);
    setRecordings(StorageService.getRecordings());
    setSelectedSession(newSession);
    setIsProcessingComplete(true);
    setOverallPercentage(100);
    setProcessingMessage('Ready for review.');
  };

  // Import existing MP4
  const handleImportFileTrigger = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const nativeFilePath = (file as any).path || '';
    const blobUrl = URL.createObjectURL(file);

    // Fast probe true video metadata (duration & resolution)
    let realDuration = 60;
    let realWidth = 1920;
    let realHeight = 1080;

    try {
      const probeVideo = document.createElement('video');
      probeVideo.preload = 'metadata';
      probeVideo.src = blobUrl;
      await new Promise<void>((resolve) => {
        probeVideo.onloadedmetadata = () => resolve();
        probeVideo.onerror = () => resolve();
        setTimeout(resolve, 2500);
      });
      if (probeVideo.duration && !isNaN(probeVideo.duration) && probeVideo.duration > 0) {
        realDuration = Math.round(probeVideo.duration);
      }
      if (probeVideo.videoWidth) {
        realWidth = probeVideo.videoWidth;
      }
      if (probeVideo.videoHeight) {
        realHeight = probeVideo.videoHeight;
      }
    } catch (err) {
      console.warn('Metadata probe warning:', err);
    }

    setCurrentScreen('processing');
    setIsProcessingComplete(false);
    setProcessingSteps(INITIAL_PROCESSING_STEPS);
    setCurrentProcessingTitle(file.name);

    await executeProcessingPipeline(
      file,
      realDuration,
      realWidth,
      realHeight,
      30,
      file.name.replace(/\.[^/.]+$/, ''),
      nativeFilePath
    );
    e.target.value = '';
  };

  const storageMetrics = StorageService.calculateStorageMetrics();

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-brand-bg text-brand-text select-none">
      {/* Global Live Recording Bar (Always visible on screen while recording is active) */}
      {isRecordingActive && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-2.5 rounded-2xl bg-black/90 backdrop-blur-xl border border-red-500/70 shadow-[0_0_35px_rgba(239,68,68,0.55)] text-white select-none animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-ping shrink-0" />
            <span className="text-xs font-black tracking-widest text-red-400 uppercase">REC</span>
          </div>

          <div className="h-4 w-px bg-white/20" />

          {/* Running Live Timer */}
          <span className="font-mono text-xl font-black text-white tabular-nums tracking-wider">
            {formatDuration(recordingState.durationSec)}
          </span>

          <div className="h-4 w-px bg-white/20" />

          {/* Direct Instant Stop Recording Button */}
          <button
            onClick={handleStopRecording}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
            title="Stop recording and isolate participants"
          >
            <Square className="w-3.5 h-3.5 fill-white" />
            <span>STOP RECORDING</span>
          </button>
        </div>
      )}

      {/* Hidden File Import Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Primary Sidebar */}
      <Sidebar
        currentScreen={currentScreen}
        onNavigate={(screen) => setCurrentScreen(screen)}
        isRecordingActive={isRecordingActive}
        theme={settings.theme}
        onToggleTheme={toggleTheme}
        storageMetrics={storageMetrics}
      />

      {/* Main App Content Viewport */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <Header
          currentScreen={currentScreen}
          onNavigate={(screen) => setCurrentScreen(screen)}
          isRecordingActive={isRecordingActive}
          onImportFile={handleImportFileTrigger}
        />

        {/* Screen Routing */}
        <main className="flex-1 flex overflow-hidden">
          {currentScreen === 'onboarding' && (
            <OnboardingScreen
              settings={settings}
              onUpdateSettings={handleUpdateSettings}
              onComplete={() => setCurrentScreen('dashboard')}
            />
          )}

          {currentScreen === 'dashboard' && (
            <DashboardScreen
              recordings={recordings}
              onNavigate={(s) => setCurrentScreen(s)}
              onSelectRecording={(rec) => {
                setSelectedSession(rec);
                setCurrentScreen('recording-details');
              }}
              onStartNewRecording={() => {
                setStudioInitialTab('screen');
                setCurrentScreen('studio');
              }}
              onRecordYourself={() => {
                setStudioInitialTab('camera');
                setCurrentScreen('studio');
              }}
              onRecordPodcast={() => {
                setPodcastInitialFormat('vodcast-single');
                setCurrentScreen('podcast-studio');
              }}
              onStartOnlineMeeting={() => {
                setPodcastInitialFormat('vodcast-online');
                setCurrentScreen('podcast-studio');
              }}
              onStartGroupMeeting={(code?: string) => {
                if (code) {
                  setJoinMeetingCode(code);
                }
                setCurrentScreen('group-meeting');
              }}
              onImportFile={handleImportFileTrigger}
              storageMetrics={storageMetrics}
            />
          )}

          {currentScreen === 'group-meeting' && (
            <GroupMeetingScreen
              settings={settings}
              onNavigate={(s) => setCurrentScreen(s)}
              initialRoomCode={joinMeetingCode || initialRoomCode || undefined}
              onMeetingRecorded={async (blob, durationSec, title, initialThumb) => {
                let thumb = initialThumb || '';
                if (!thumb) {
                  try {
                    thumb = await generateThumbnailFromBlob(blob);
                  } catch (e) {
                    console.warn('Failed to extract thumbnail', e);
                  }
                }

                let savedFilePath = `GroupMeeting_${Date.now()}.webm`;
                if (window.electronAPI && typeof window.electronAPI.saveRecordingBlob === 'function') {
                  try {
                    const buf = await blob.arrayBuffer();
                    const saveRes = await window.electronAPI.saveRecordingBlob(
                      generateId('gm'),
                      `GroupMeeting_${Date.now()}.webm`,
                      buf
                    );
                    if (saveRes && saveRes.filePath) {
                      savedFilePath = saveRes.filePath;
                    }
                  } catch (err) {
                    console.warn('Could not save recording blob to desktop folder:', err);
                  }
                }

                const newSession: RecordingSession = {
                  id: generateId('group_meet'),
                  title: title || 'Group Meeting Session',
                  createdAt: new Date().toISOString(),
                  durationSec,
                  fileSize: blob.size,
                  filePath: savedFilePath,
                  blobUrl: URL.createObjectURL(blob),
                  thumbnailUrl: thumb || undefined,
                  resolution: { width: 1920, height: 1080 },
                  fps: 30,
                  audioChannels: 2,
                  status: 'ready',
                  detectedLayout: 'grid_2x2',
                  isGroupMeeting: true,
                  isSingleRecording: true,
                  recordingMode: 'single-composite',
                  tags: ['group-meeting', 'single-recording'],
                  participants: []
                };
                StorageService.addRecording(newSession);
                setSelectedSession(newSession);
                setRecordings(StorageService.getRecordings());
                setCurrentScreen('recording-details');
              }}
            />

          )}

          {currentScreen === 'scheduler' && (
            <SchedulerScreen
              onNavigate={(s) => setCurrentScreen(s)}
              onLaunchPodcastStudio={(fmt) => {
                if (fmt) setPodcastInitialFormat(fmt);
                setCurrentScreen('podcast-studio');
              }}
              onLaunchGroupMeeting={(code) => {
                if (code) setJoinMeetingCode(code);
                setCurrentScreen('group-meeting');
              }}
            />
          )}

          {currentScreen === 'podcast-studio' && (
            <PodcastStudioScreen
              settings={settings}
              onNavigate={(s) => setCurrentScreen(s)}
              onSelectSession={(sess) => {
                setSelectedSession(sess);
                setRecordings(StorageService.getRecordings());
              }}
              availableDiskSpaceGb={availableDiskSpaceGb}
              initialFormatMode={podcastInitialFormat}
            />
          )}

          {currentScreen === 'studio' && (
            <RecordingStudioScreen
              settings={settings}
              onStartRecording={handleStartRecording}
              availableDiskSpaceGb={availableDiskSpaceGb}
              initialTab={studioInitialTab}
            />
          )}

          {currentScreen === 'active-recording' && (
            <ActiveRecordingScreen
              recordingState={recordingState}
              onPause={handlePauseRecording}
              onResume={handleResumeRecording}
              onStop={handleStopRecording}
              stream={activeStream}
              availableDiskSpaceGb={availableDiskSpaceGb}
              aspectRatio={recordingAspectRatio}
              onToggleScreenShare={activeSourceType === 'camera' ? handleToggleScreenShare : undefined}
              isCameraMode={activeSourceType === 'camera'}
            />
          )}

          {currentScreen === 'processing' && (
            <ProcessingStudioScreen
              steps={processingSteps}
              currentStepIndex={processingIndex}
              overallPercentage={overallPercentage}
              currentMessage={processingMessage}
              onCancel={() => setCurrentScreen('dashboard')}
              onProceedToReview={() => setCurrentScreen('separate-videos')}
              isComplete={isProcessingComplete}
              videoTitle={currentProcessingTitle}
            />
          )}

          {currentScreen === 'separate-videos' && (
            <SeparateVideosScreen
              session={selectedSession || (recordings.length > 0 ? recordings[0] : null)}
              recordings={recordings}
              onSelectSession={(rec) => setSelectedSession(rec)}
              onNavigateToEditor={() => setCurrentScreen('participant-review')}
              onProceedToExport={() => setCurrentScreen('export-center')}
              onImportFile={handleImportFileTrigger}
              onUpdateSession={(updated) => {
                StorageService.updateRecording(updated.id, updated);
                setSelectedSession(updated);
                setRecordings(StorageService.getRecordings());
              }}
            />
          )}

          {currentScreen === 'participant-review' && (
            <ParticipantReviewScreen
              session={selectedSession || (recordings.length > 0 ? recordings[0] : null)}
              recordings={recordings}
              onSelectSession={(rec) => setSelectedSession(rec)}
              onNavigateToStudio={() => setCurrentScreen('studio')}
              onImportFile={handleImportFileTrigger}
              onUpdateSession={(updated) => {
                StorageService.updateRecording(updated.id, updated);
                setSelectedSession(updated);
                setRecordings(StorageService.getRecordings());
              }}
              onProceedToExport={() => setCurrentScreen('export-center')}
              onNavigateToSeparateVideos={() => setCurrentScreen('separate-videos')}
            />
          )}

          {currentScreen === 'recording-details' && (
            <RecordingDetailsScreen
              session={selectedSession || (recordings.length > 0 ? recordings[0] : null)}
              onBack={() => setCurrentScreen('library')}
              onOpenReview={() => setCurrentScreen('separate-videos')}
              onOpenExport={() => setCurrentScreen('export-center')}
              onReprocess={() => {
                const sess = selectedSession || (recordings.length > 0 ? recordings[0] : null);
                if (!sess) return;
                setCurrentScreen('processing');
                executeProcessingPipeline(
                  new Blob([], { type: 'video/mp4' }),
                  sess.durationSec,
                  sess.resolution.width,
                  sess.resolution.height,
                  sess.fps
                );
              }}
              onDelete={(id) => {
                const updated = StorageService.deleteRecording(id);
                setRecordings(updated);
                setSelectedSession(updated.length > 0 ? updated[0] : null);
                setCurrentScreen('library');
              }}
            />
          )}

          {currentScreen === 'export-center' && (
            <ExportCenterScreen
              session={selectedSession || (recordings.length > 0 ? recordings[0] : null)}
              defaultOutputFolder={settings.storagePath}
            />
          )}

          {currentScreen === 'library' && (
            <RecordingLibraryScreen
              recordings={recordings}
              onSelectRecording={(rec) => {
                setSelectedSession(rec);
                setCurrentScreen('recording-details');
              }}
              onUpdateRecording={(id, updates) => {
                StorageService.updateRecording(id, updates);
                setRecordings(StorageService.getRecordings());
              }}
              onDeleteRecording={(id) => {
                const updated = StorageService.deleteRecording(id);
                setRecordings(updated);
                if (selectedSession?.id === id) {
                  setSelectedSession(updated.length > 0 ? updated[0] : null);
                }
              }}
              onStartNewRecording={() => setCurrentScreen('studio')}
              onNavigate={(screen) => setCurrentScreen(screen)}
            />
          )}

          {currentScreen === 'settings' && (
            <SettingsScreen
              settings={settings}
              onUpdateSettings={handleUpdateSettings}
              availableDiskSpaceGb={availableDiskSpaceGb}
            />
          )}
        </main>
      </div>
    </div>
  );
};

const GuestAppWrapper: React.FC<{
  initialTab?: 'group' | 'podcast';
  initialRoomCode?: string;
}> = ({ initialTab, initialRoomCode }) => {
  const [settings] = useState<AppSettings>(() => StorageService.getSettings());

  return (
    <MobileGuestHubScreen
      settings={settings}
      initialTab={initialTab}
      initialRoomCode={initialRoomCode}
      onMeetingRecorded={async (blob, durationSec, title, initialThumb) => {
        let thumb = initialThumb || '';
        if (!thumb) {
          try {
            thumb = await generateThumbnailFromBlob(blob);
          } catch (e) {
            console.warn('Failed to extract thumbnail', e);
          }
        }
        const newSession: RecordingSession = {
          id: generateId('group_meet'),
          title: title || 'Group Meeting Session',
          createdAt: new Date().toISOString(),
          durationSec,
          fileSize: blob.size,
          filePath: `GroupMeeting_${Date.now()}.webm`,
          blobUrl: URL.createObjectURL(blob),
          thumbnailUrl: thumb || undefined,
          resolution: { width: 1920, height: 1080 },
          fps: 30,
          audioChannels: 2,
          status: 'ready',
          detectedLayout: 'grid_2x2',
          isGroupMeeting: true,
          isSingleRecording: true,
          recordingMode: 'single-composite',
          tags: ['group-meeting', 'single-recording'],
          participants: []
        };
        StorageService.addRecording(newSession);
      }}
    />
  );
};

export const App: React.FC = () => {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const urlMeetingCode = urlParams?.get('meet') || urlParams?.get('group');
  const isGroupMeetingMode = Boolean(urlParams?.get('mode') === 'group-meeting' || urlMeetingCode);

  // Check if running as an installed PWA (Standalone Desktop/Mobile Window)
  const isInstalledPwa = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );

  // Explicit Guest mode from query param, extension or mobile APK
  const isExplicitGuest = typeof window !== 'undefined' && (
    Boolean((window as any).isGuestApp) ||
    Boolean((window as any).IS_GUEST_APP) ||
    Boolean((window as any).electronAPI?.isGuest) ||
    urlParams?.get('mode') === 'guest-app' ||
    urlParams?.get('mode') === 'guest' ||
    urlParams?.get('mode') === 'group-meeting' ||
    Boolean(urlMeetingCode) ||
    window.location.protocol === 'chrome-extension:' ||
    Capacitor.isNativePlatform() ||
    Capacitor.getPlatform() === 'android'
  );

  const isMobileGuest = Boolean(isInstalledPwa || isExplicitGuest);
  const isWebDeployment = typeof window !== 'undefined' && !(window as any).electronAPI && window.location.protocol !== 'file:';

  const [inGuestHub, setInGuestHub] = useState<boolean>(() => {
    return isMobileGuest;
  });

  const [showLanding, setShowLanding] = useState<boolean>(() => {
    if (isMobileGuest || isInstalledPwa || isExplicitGuest) return false;
    if (urlParams?.get('mode') === 'host' || urlParams?.get('mode') === 'studio') return false;
    return isWebDeployment;
  });

  if (showLanding && !inGuestHub) {
    return (
      <LandingScreen
        onEnterGuestHub={() => {
          setShowLanding(false);
          setInGuestHub(true);
        }}
        onEnterStudio={() => {
          setShowLanding(false);
          setInGuestHub(false);
        }}
      />
    );
  }

  if (inGuestHub || isMobileGuest) {
    return (
      <GuestAppWrapper
        initialTab={isGroupMeetingMode ? 'group' : undefined}
        initialRoomCode={urlMeetingCode || undefined}
      />
    );
  }

  return (
    <HostStudioApp
      initialScreen={isGroupMeetingMode || urlMeetingCode ? 'group-meeting' : 'dashboard'}
      initialRoomCode={urlMeetingCode || undefined}
    />
  );
};

export default App;

